// The task pages' scene windows (SCENES B9, restyled by mock-website/round-6/plan/APP-R3.md §4): one small window at
// the top of each task page's side column showing the matching benefit card's isometric scene from the landing page,
// on the #212423 window ground, with its caption and a Pause button outside it. Decoration only: aria-hidden, no
// pointer reaction, never wired to account or transaction state. The art is inline SVG markup generated by
// mock-website/round-6/tools/export-app-art.mjs (presentation attributes only, CSP-safe), driven by the card's own
// art module (apply(u), u 0 rest → 1 active), which writes attributes only.
//
// The loop (B9 "Idle loop", APP-R3 §4): rest 40 % of the period, the card's gesture stretched to 900 ms, hold 30 %,
// the return, rest; periods 9000 / 7200 / 8400 / 9600 ms. It starts 600 ms after the window is first at least half
// visible and stops offscreen or in a hidden tab (back to the rest frame, restarting at t = 0). Pause ("Pause motion"
// / "Play motion") shows the rest frame; every copy shares one store. Dimming follows what the page shows (the review
// panel, the Convert SOL tab, "Staking is off"): the window fades to .45, its loop runs on at 0.4x to the rest frame
// and holds; undimmed, it fades back and restarts. Reduced motion: the rest frame only, no Pause button.
import { type RefObject, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { SCENES, type SceneId } from '../art/zq/scenes';
import type { SceneArt } from '../art/zq/runtime/types';

/** SCENES §10 "App captions" and "App, Convert tab". */
const CAPTIONS: Record<SceneId, string> = {
  deposit: 'Assets arrive in your vault.',
  send: 'From your vault to their wallet.',
  swap: 'Exchange assets inside your vault.',
  stake: 'Stake SOL from your vault.',
};
const CONVERT_CAPTION = 'Convert SOL stays inside your vault.';

const PERIOD: Record<SceneId, number> = { deposit: 9000, send: 7200, swap: 8400, stake: 9600 };
const GESTURE = 900;

/** The phases of one loop: 0 rest, 1 forward, 2 hold, 3 return, 4 rest tail; and the clock u at time t. */
function at(scene: SceneId, t: number): { phase: number; u: number } {
  const P = PERIOD[scene], r = 0.4 * P, h = 0.3 * P;
  if (t < r) return { phase: 0, u: 0 };
  if (t < r + GESTURE) return { phase: 1, u: (t - r) / GESTURE };
  if (t < r + GESTURE + h) return { phase: 2, u: 1 };
  if (t < r + 2 * GESTURE + h) return { phase: 3, u: 1 - (t - r - GESTURE - h) / GESTURE };
  return { phase: 4, u: 0 };
}

async function bind(scene: SceneId, svg: SVGSVGElement): Promise<SceneArt> {
  switch (scene) {
    case 'deposit':
      return (await import('../art/zq/runtime/art-deposit.js')).art(svg);
    case 'send':
      return (await import('../art/zq/runtime/art-send.js')).art(svg);
    case 'swap':
      return (await import('../art/zq/runtime/art-swap.js')).art(svg);
    case 'stake':
      return (await import('../art/zq/runtime/stake-art.js')).stakeArt(svg);
  }
}

// ---------------------------------------------------------------- the shared Pause store (every copy in step)

const PAUSE_KEY = 'zq-scene-paused';
let paused = (() => {
  try {
    return localStorage.getItem(PAUSE_KEY) === '1';
  } catch {
    return false;
  }
})();
const listeners = new Set<() => void>();
function setPaused(next: boolean) {
  paused = next;
  try {
    localStorage.setItem(PAUSE_KEY, next ? '1' : '0');
  } catch {
    // storage blocked: the choice lasts for this page
  }
  for (const l of listeners) l();
}
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const usePaused = () => useSyncExternalStore(subscribe, () => paused);

function useReducedMotion(): boolean {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = useState(() => matchMedia(query).matches);
  useEffect(() => {
    const m = matchMedia(query);
    const on = () => setReduced(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return reduced;
}

/** What the task page shows (never chain events): the review panel, the Convert SOL tab, the "Staking is off" notice. */
function usePageFocus(card: RefObject<HTMLDivElement | null>): { dim: boolean; convert: boolean } {
  const [focus, setFocus] = useState({ dim: false, convert: false });
  useEffect(() => {
    const task = card.current?.closest('.task');
    if (!task) return;
    const read = () => {
      const review = !!task.querySelector('.task-main section.review');
      const convert = task.querySelector('#unwrap-tab')?.getAttribute('aria-selected') === 'true';
      const off = [...task.querySelectorAll('.task-main .notice > .eyebrow')].some((e) => e.textContent === 'Staking is off');
      setFocus((f) => (f.dim === (review || convert || off) && f.convert === convert ? f : { dim: review || convert || off, convert }));
    };
    read();
    const mo = new MutationObserver(read);
    mo.observe(task, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-selected'] });
    return () => mo.disconnect();
  }, [card]);
  return focus;
}

export function SceneCard({ scene }: { scene: SceneId }) {
  const card = useRef<HTMLDivElement>(null);
  const win = useRef<HTMLDivElement>(null);
  const isPaused = usePaused();
  const reduced = useReducedMotion();
  const { dim, convert } = usePageFocus(card);
  const live = useRef({ dim, paused: isPaused, reduced });
  live.current = { dim, paused: isPaused, reduced };
  const control = useRef<{ restart: () => void } | null>(null);

  useEffect(() => {
    const svg = win.current?.querySelector('svg');
    if (!svg) return;
    let art: SceneArt | null = null;
    let raf = 0;
    let timer = 0;
    let visible = false;
    let t = 0; // the loop's own clock, ms (advanced by frame time, 0.4x while dimmed)
    let last = 0;
    let phase = 0;
    let u = 0;
    let alive = true;

    const toRest = () => {
      if (!art) return;
      if (u > 0) art.reverse(-1, u);
      u = 0;
      phase = 0;
      t = 0;
      art.apply(0);
    };
    const stop = () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
      raf = 0;
      timer = 0;
    };
    const frame = (now: number) => {
      raf = 0;
      if (!art) return;
      const dt = last ? Math.min(100, now - last) : 0;
      last = now;
      const { dim: dimmed } = live.current;
      // dimmed: finish the loop at 0.4x, then hold the rest frame
      if (dimmed && (phase === 0 || phase === 4)) {
        toRest();
        return;
      }
      t = (t + dt * (dimmed ? 0.4 : 1)) % PERIOD[scene];
      const next = at(scene, t);
      if (next.phase !== phase) {
        if (next.phase === 1) art.reverse(1, 0);
        if (next.phase === 3) art.reverse(-1, 1);
        phase = next.phase;
      }
      if (next.u !== u) {
        u = next.u;
        art.apply(u);
      }
      raf = requestAnimationFrame(frame);
    };
    const canRun = () => alive && art && visible && !document.hidden && !live.current.paused && !live.current.reduced && !live.current.dim;
    const start = (delay: number) => {
      stop();
      if (!canRun()) return;
      timer = window.setTimeout(() => {
        timer = 0;
        if (!canRun()) return;
        last = 0;
        raf = requestAnimationFrame(frame);
      }, delay);
    };
    const restart = () => {
      // a pause, a dim that finished, a hidden tab or an offscreen window resumes from the rest frame at t = 0
      if (!live.current.dim) {
        stop();
        toRest();
      }
      start(live.current.dim ? 0 : 600);
    };
    control.current = {
      restart: () => {
        if (live.current.dim) {
          // keep running (at 0.4x) until the loop reaches rest; frame() then holds the rest frame
          if (!raf && !timer && phase !== 0 && phase !== 4 && art && visible && !document.hidden && !live.current.paused) raf = requestAnimationFrame(frame);
          return;
        }
        restart();
      },
    };

    const io = new IntersectionObserver(
      ([e]) => {
        const was = visible;
        visible = !!e && e.intersectionRatio >= 0.5;
        if (visible && !was) restart();
        if (!visible && was) {
          stop();
          toRest();
        }
      },
      { threshold: [0, 0.5] },
    );
    const onVisibility = () => {
      if (document.hidden) {
        stop();
        toRest();
      } else restart();
    };
    document.addEventListener('visibilitychange', onVisibility);

    void bind(scene, svg as SVGSVGElement).then((a) => {
      if (!alive) return;
      art = a;
      art.apply(0);
      io.observe(win.current!);
      if (import.meta.env.DEV) {
        // review only: pose the window at time ms of its loop (forward poses)
        (window as unknown as { __zqScene?: (ms: number) => void }).__zqScene = (ms: number) => {
          stop();
          const p = at(scene, ms % PERIOD[scene]);
          art?.pose(p.phase === 3 || p.phase === 2 ? 1 : p.u);
        };
      }
    });
    return () => {
      alive = false;
      stop();
      io.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      control.current = null;
    };
  }, [scene]);

  // pause, reduced motion and the page's focus change what the loop may do
  useEffect(() => {
    control.current?.restart();
  }, [isPaused, reduced, dim]);

  return (
    <div className="scene-card" ref={card}>
      <div ref={win} className={`scene-window ${scene}${dim ? ' dim' : ''}`} aria-hidden="true" dangerouslySetInnerHTML={{ __html: SCENES[scene] }} />
      <div className="scene-caption-row">
        <p className="scene-caption">{scene === 'send' && convert ? CONVERT_CAPTION : CAPTIONS[scene]}</p>
        {reduced ? null : (
          <button type="button" className="scene-pause" onClick={() => setPaused(!isPaused)}>
            <span className="sr-only">{isPaused ? 'Play motion' : 'Pause motion'}</span>
            <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
              {isPaused ? <path d="M5 3.5v9l7.5-4.5z" fill="currentColor" /> : <path d="M4.5 3.5h2.5v9H4.5zM9 3.5h2.5v9H9z" fill="currentColor" />}
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
